import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";

const Section = ({ num, title, children, testid }) => (
  <Reveal delay={0.05}>
    <div data-testid={testid}>
      <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight flex gap-4 items-baseline">
        <span className="font-mono text-xs text-ember">{num}</span>
        {title}
      </h2>
      <div className="mt-4 text-slate-600 leading-relaxed pl-9 space-y-4">{children}</div>
    </div>
  </Reveal>
);

const Privacy = () => (
  <>
    <PageHeader
      testid="privacy-header"
      overline="Privacy Policy"
      title="Your information,"
      accent="protected."
      description="How Concorde Auto Acceptance collects, uses, shares, and safeguards your personal information."
    />
    <section className="pb-24 lg:pb-32" data-testid="privacy-body">
      <div className="mx-auto max-w-3xl px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.2em] text-slate-600 uppercase" data-testid="privacy-effective-date">
            Last updated 10/16/2026
          </p>
        </Reveal>
        <div className="mt-12 space-y-14">
          <Reveal>
            <div className="space-y-4 text-slate-600 leading-relaxed" data-testid="privacy-intro">
              <p>
                Concorde Auto Acceptance knows that you care how the
                information collected about you is used and shared. At Concorde
                Auto Acceptance, we understand that the confidentiality
                and protection of your personal information is one of our
                fundamental responsibilities. While Concorde Auto
                Acceptance uses appropriate security measures to safeguard details
                of your experience, we do collect and use information from your
                visit, though, so please read on for more details.
              </p>
              <p>
                By using this site, you agree to the terms of Concorde
                Auto Acceptance's privacy policy. For other terms and conditions
                of using this site,{" "}
                <Link
                  to="/terms-of-use"
                  className="text-ember font-medium hover:underline"
                  data-testid="privacy-terms-link"
                >
                  click here for our Terms of Use
                </Link>
                .
              </p>
            </div>
          </Reveal>

          <Section num="01" title="Information Collection and Use" testid="privacy-section-0">
            <p>
              We collect two main categories of information from or about you:
              personal information and non-personally identifiable information
              ("NPI").
            </p>
            <p>
              <strong className="text-slate-900">Personal Information</strong> is
              information that directly identifies you as an individual, like your
              name, residential address, email address, social security number,
              date of birth, driver's license number, phone number, credit or
              debit card number, account number and/or password.
            </p>
            <p>
              <strong className="text-slate-900">Non-personally Identifiable Information</strong>,
              or NPI, is information about an individual or categories or groups
              of individuals that cannot be used on its own to identify you as an
              individual, like your IP address, randomly assigned values in
              "cookies," mobile device identifiers, geo-location, and other
              technical information. We also collect additional information from
              you that is not technical, but cannot be used on its own to identify
              you as an individual, such as your gender, vehicle information,
              occupation and other employment information, personal references,
              and residence-related information such as lease and ownership
              information.
            </p>
            <p>
              In general, you can visit our website without telling us who you are
              or revealing personal information about yourself. When you use the
              website, your name, telephone number, street or email address, and
              other personally identifying information may be requested or
              collected but is not required.
            </p>
            <p>Information we collect from you may include:</p>
            <ul className="list-disc list-inside space-y-1.5">
              <li>Your name</li>
              <li>Your e-mail address</li>
              <li>Home address</li>
              <li>Telephone number, including mobile number</li>
              <li>SMS/text message consent and opt-in records</li>
              <li>URL</li>
              <li>Credit card information</li>
              <li>Other billing information</li>
              <li>Any other information you choose to disclose</li>
            </ul>
            <p>Information we receive from a consumer-reporting agency</p>
            <ul className="list-disc list-inside space-y-1.5">
              <li>Your payment history, if you have previously purchased a vehicle from us</li>
            </ul>
            <p>
              Our servers may also automatically collect NPI about you, your
              online behavior and your computer, mobile, or other device. The
              information may include, without limitation, information about the
              device you are using, such as the make, model, settings, and
              specification of the device, and may also include the geographic
              location of you or your device, as well as the date/time stamp, IP
              address, pages visited, time of visits, content viewed, ads viewed,
              the site(s), application(s), destination(s), or service(s) you
              arrived from, and other clickstream data.
            </p>
            <p>
              Concorde Auto Acceptance will use these technologies to
              measure the effectiveness of our marketing campaigns and to
              recognize your browser for the most effective delivery or placement
              of advertisements.
            </p>
            <p>
              If you choose to access, visit or use any third-party service(s)
              that may be integrated with or linked to the Concorde
              Auto Acceptance website, we may receive personal information and
              other NPI about you and your computer, mobile or other device that
              you have made available to those services, including information
              about your contacts on those services. Your decision to use
              third-party service(s) in connection with Concorde Auto
              Acceptance is voluntary. Concorde Auto Acceptance does
              not operate the third-party service(s) or links and is not
              responsible for the privacy protection or content outside of the
              Concorde Auto Acceptance website.
            </p>
          </Section>

          <Section num="02" title='Local Device Storage ("Cookies") and Tracking Technologies' testid="privacy-section-1">
            <p>
              A cookie is a data file that a website will store on your computer
              or mobile device so the site can remember you at a later time.
              Cookies can make a website more useful by allowing the site to
              personalize information for visitors and by storing information
              about user preferences so that the user does not have to re-enter
              information each time he or she returns to the site. Cookies can
              also be used to track user preferences and webpages used while
              visiting the website. You may be able to remove, reject and/or
              disable some types of cookies using your browser's preferences or
              other programs, but this may impact the functionality of the website
              and affect your usage. Please check your browser to determine where
              these types of cookies are stored and how they can be deleted or
              disabled.
            </p>
            <p>
              Other third parties may also use tracking technologies to serve you
              advertisements tailored to interests you have shown by browsing on
              this site and other sites you have visited, and for other lawful
              business purposes. The use of tracking technologies by third parties
              is subject to their own privacy policies, not this privacy policy,
              and we have no responsibility or liability in connection with the
              use of tracking technologies by third parties.
            </p>
          </Section>

          <Section num="03" title="Information Use" testid="privacy-section-2">
            <p>
              We do not disclose any personal information about our customers or
              former customers to anyone, except as permitted by law. Federal law
              gives consumers the right to limit some, but not all, sharing.{" "}
              <a
                href="https://images.jazelc.com/uploads/autocitycredit/PrivacyNotice_Legal_MailIn_NoAffil-1.pdf"
                className="text-ember font-medium hover:underline"
                data-testid="privacy-flyer-link"
              >
                Click here
              </a>{" "}
              for Concorde Auto Acceptance's Privacy Policy flyer.
            </p>
            <p>
              We use personal information you supply to provide you with the
              content you have requested. We may also use the information to
              communicate with you about new features, products or services, or to
              improve the services that we offer by tailoring them to your needs.
            </p>
            <p>
              We may share information about you, your computer, mobile or other
              device, with our parent, subsidiaries, and affiliates. There are
              also times when you may provide information about yourself to us in
              areas that may be managed or participated in by third parties. In
              such cases, the information may be used by us and such third
              party(ies), each pursuant to its own policies. While we may seek to
              require such third parties to follow appropriate privacy policies
              and will not authorize them to use this information except for the
              express purpose for which it is provided, we are not responsible for
              any actions or policies of third parties.
            </p>
            <p>
              We may disclose certain information to government agencies,
              consumer-reporting agencies, and other outside parties as permitted
              or required by the Federal Privacy Act and other applicable laws,
              for example, to cooperate with governmental, law enforcement, or
              other investigation(s) or legal proceedings.
            </p>
            <p>
              We reserve the right to use the information we collect about you,
              your computer, mobile or other device, including geographic
              information, which may at times be able to identify you, for any
              lawful business purpose, including without limitation, to help
              diagnose problems with our servers, to gather broad demographic
              information, analyze trends, track users' movements around the
              website, and to otherwise administer the website. We and our
              service providers use analytics to analyze traffic to this website
              to help us understand our customers' and visitors' needs and to
              continually improve our site. Some of the analytics use the
              tracking technologies described above. Text messaging originator
              opt-in data and consent will not be sold, shared, or disclosed to
              any third parties or affiliates for marketing or promotional
              purposes. SMS data may only be shared with service providers as
              necessary to process and deliver text messages.
            </p>
          </Section>

          <Section num="04" title="Information Security" testid="privacy-section-3">
            <p>
              At Concorde Auto Acceptance, we have implemented
              security measures and procedures that we believe to be reasonable
              and appropriate to protect the security, confidentiality and
              integrity of information that you or third parties have provided to
              us. While we regularly review and assess the sufficiency of our
              security measures to protect against loss, misuse, or alteration of
              said information, we cannot guarantee or warrant the security of any
              information you disclose or transmit to us online. By using the
              website, you agree to assume all risk in connection with the
              information sent to us or collected by us when you access, visit or
              use the website, including without limitation your personal
              information or NPI, and we are not responsible for any loss of such
              information or the consequences thereof.
            </p>
            <p>
              In the unlikely event that we believe the security of your
              information in our possession or control may have been compromised,
              we may seek to notify you. If notification is appropriate, we may
              notify you via your computer, mobile or other device.
            </p>
          </Section>

          <Section num="05" title="Mobile Subscriber Information" testid="privacy-section-4">
            <p>
              Your mobile information will <strong className="text-slate-900">NEVER</strong> be
              shared with other parties under any circumstances. This includes no
              sharing with third parties, affiliates, or for marketing or
              promotional purposes.
            </p>
          </Section>

          <Section num="06" title="Text Messages" testid="privacy-section-5">
            <p>
              You authorize us to contact you, including by sending text messages
              directly or through a conduit text messaging service and other
              communications to a cell phone using an automatic telephone dialing
              system or an artificial or prerecorded voice message, at any number
              you provide. Text messages we send may include requests for
              payment, requests for a call back, and account status
              notifications. Message and data rates may apply. Message frequency
              varies. Reply STOP to opt out at any time. Reply HELP for help. You
              may revoke your consent for us to contact you at a specified
              telephone number by communicating your revocation to us through any
              reasonable means.
            </p>
            <p>
              When you fill out the contact form on our site, complete intake
              forms, e-mail us, call us, inquire about a vehicle, inquire about
              financing a vehicle, or inquire about services we offer or specials
              we maintain, you are authorizing us to contact you, including by
              sending text messages directly or through a conduit text messaging
              service and other communications to a cell phone using an automatic
              telephone dialing system or an artificial or prerecorded voice
              message, at any number you provide. You may revoke your consent for
              us to contact you at a specified telephone number by communicating
              your revocation to us through any reasonable means. To opt out of
              text messages, reply STOP to any message at any time. You will
              receive one final message confirming your opt-out status.
            </p>
          </Section>

          <Section num="07" title="Notification of Changes" testid="privacy-section-6">
            <p>
              Given the rapid changes in offerings and technologies, we reserve
              the right to change, modify, add or remove portions of our Privacy
              Policy at any time without prior notice. If these changes are
              substantial, we will notify you via email. You are free to review
              our Privacy Policy for changes at any time.
            </p>
          </Section>

          <Section num="08" title="Contacting the Web Site and the Compliance Officer" testid="privacy-section-7">
            <p>
              If you have any questions about this privacy statement, please
              contact:
            </p>
            <p>
              Compliance Officer, 908 Buckner, Dallas, TX 75271 or{" "}
              <a
                href="mailto:bingram@autocitycredit.com"
                className="text-ember font-medium hover:underline"
                data-testid="privacy-compliance-email"
              >
                bingram@autocitycredit.com
              </a>
              . Please include enough information so that we are able to help you,
              such as your name, phone number or email address, and the specific
              website, mobile site, application, and/or other service you are
              contacting us about.
            </p>
            <p>
              If you don't agree to the terms contained in this Privacy Policy,
              you must immediately exit the website.
            </p>
          </Section>
        </div>
      </div>
    </section>
  </>
);

export default Privacy;
